import { Link } from 'react-router-dom'
import {CheckCircle2, Clock, Shield, Sparkles, ChevronDown, ChevronUp} from 'lucide-react'
import { useState } from 'react'
import Layout from '@/components/Layout'
import { AUDIT_PACKAGES } from '@/lib/mockData'

const C = {
  bgDeep:     '#000000',
  surface:    '#070a0c',
  bgCard:     '#0a0e12',
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.12)',
  borderTurq: 'rgba(26,214,196,0.30)',
  border:     'rgba(255,255,255,0.08)',
  borderMed:  'rgba(255,255,255,0.14)',
  textSecond: '#b4c0cc',
  textMuted:  '#6a7682',
}

const FAQS = [
  {
    q: 'Which blockchains do you support?',
    a: 'We audit smart contracts on all major EVM-compatible chains including Ethereum, BNB Chain (BSC), Polygon, Arbitrum, Base, Optimism, and Avalanche.',
  },
  {
    q: 'How long does an audit take?',
    a: 'Every audit has a 7–10 business day turnaround from the time we receive your contract address and project details.',
  },
  {
    q: 'What do I receive when the audit is complete?',
    a: 'You receive a verified badge displayed on your public project profile, a security score from 1–100, a full vulnerability report with severity classifications, an audit verdict (Passed / Failed), a downloadable PDF report, and a shareable social media card.',
  },
  {
    q: 'How long does the launch promo last?',
    a: 'The $150 launch promo price is available for the first 60 days. After the promo period ends, the audit is priced at the standard $250.',
  },
  {
    q: 'Do you offer re-audits after fixes?',
    a: 'Yes. If your contract fails the audit, you can remediate the findings and submit for a re-audit at a discounted rate. Contact us for pricing.',
  },
]

function FAQ({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{ background: C.bgCard, border: `1px solid ${open ? C.borderTurq : C.border}` }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-white/[0.02]"
      >
        <span className="text-sm font-medium text-white">{q}</span>
        {open
          ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: C.turq }} />
          : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: C.textMuted }} />
        }
      </button>
      {open && (
        <div className="px-5 pb-4 border-t" style={{ borderColor: C.border }}>
          <p className="text-sm leading-relaxed pt-3" style={{ color: C.textMuted }}>{a}</p>
        </div>
      )}
    </div>
  )
}

export default function Pricing() {
  const pkg = AUDIT_PACKAGES[0]
  const hasPromo = typeof pkg.promoPriceUsd === 'number'

  return (
    <Layout>
      {/* Header */}
      <section style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-[900px] mx-auto px-5 py-16 text-center">
          <span
            className="text-xs font-semibold tracking-widest uppercase mb-3 inline-block"
            style={{ color: C.turq, letterSpacing: '0.12em' }}
          >
            Audit Package
          </span>
          <h1
            className="text-white font-light leading-tight mb-4"
            style={{ fontSize: 'clamp(28px, 5vw, 48px)', letterSpacing: '-1px' }}
          >
            One Audit. One Price.
          </h1>
          <p className="text-sm max-w-xl mx-auto leading-relaxed" style={{ color: C.textMuted }}>
            Professional smart contract audits by Mandox Audits — including a verified badge, security score, and full vulnerability report. Delivered in <span style={{ color: C.turq, fontWeight: 500 }}>7–10 business days</span>.
          </p>
        </div>
      </section>

      {/* Package */}
      <section style={{ background: C.bgDeep }} className="py-14">
        <div className="max-w-[460px] mx-auto px-5">
          {/* Promo banner */}
          {hasPromo && (
            <div
              className="flex flex-col items-center justify-center gap-1.5 px-6 py-5 rounded-2xl mb-6 animate-pulse"
              style={{ background: `linear-gradient(135deg, rgba(26,214,196,0.22) 0%, rgba(26,214,196,0.10) 100%)`, border: `1.5px solid ${C.borderTurq}`, boxShadow: `0 0 32px rgba(26,214,196,0.18)` }}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: '#ffffff' }} />
                <span className="text-base font-bold tracking-wide" style={{ color: '#ffffff' }}>Launch Promo — First 60 Days Only</span>
                <Sparkles className="w-5 h-5" style={{ color: '#ffffff' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: '#ffffff' }}>Get your audit for <span className="font-bold">$150</span> instead of $250 — save $100</p>
            </div>
          )}

          <div
            className="relative rounded-2xl flex flex-col overflow-hidden"
            style={{
              background: 'linear-gradient(160deg, #071614 0%, #030e0c 100%)',
              border: `1px solid ${C.borderTurq}`,
              boxShadow: `0 0 50px rgba(26,214,196,0.12)`,
            }}
          >
            <div className="flex flex-col flex-1 p-8">
              {/* Icon + name */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                >
                  <Shield className="w-5 h-5" style={{ color: C.turq }} />
                </div>
                <div>
                  <p className="text-base font-semibold text-white">{pkg.name}</p>
                  <p className="text-xs" style={{ color: C.textMuted }}>{pkg.turnaround}</p>
                </div>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-end gap-2 flex-wrap">
                  <span
                    className="text-5xl font-light leading-none"
                    style={{ color: C.turq, letterSpacing: '-2px' }}
                  >
                    ${(hasPromo ? pkg.promoPriceUsd! : pkg.priceUsd).toLocaleString()}
                  </span>
                  <span className="text-sm mb-1" style={{ color: C.textMuted }}>USD</span>
                  {hasPromo && (
                    <span
                      className="text-xl font-light mb-0.5 line-through"
                      style={{ color: C.textMuted }}
                    >
                      ${pkg.priceUsd.toLocaleString()}
                    </span>
                  )}
                </div>
                {hasPromo && (
                  <p className="text-xs mt-2" style={{ color: C.turq }}>
                    Save ${(pkg.priceUsd - pkg.promoPriceUsd!).toLocaleString()} during the launch promo
                  </p>
                )}
              </div>

              {/* Turnaround chip */}
              <div
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full mb-6 w-fit"
                style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
              >
                <Clock className="w-3 h-3" />
                {pkg.turnaround}
              </div>

              {/* Features */}
              <ul className="flex flex-col gap-3 mb-6">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: C.textSecond }}>
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.turq }} />
                    {f}
                  </li>
                ))}
              </ul>

              {/* Mandox promotion callout */}
              <div
                className="flex items-start gap-3 rounded-xl px-4 py-3.5 mb-8"
                style={{ background: 'rgba(26,214,196,0.06)', border: `1px solid rgba(26,214,196,0.20)` }}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" style={{ color: C.turq }}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.254 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                </svg>
                <p className="text-xs leading-relaxed" style={{ color: C.textSecond }}>
                  Your audit will be <span className="font-semibold" style={{ color: '#ffffff' }}>promoted across all Mandox ecosystem X pages</span> — reaching a combined audience of <span className="font-semibold" style={{ color: C.turq }}>30,000+ followers</span>.
                </p>
              </div>

              {/* CTA */}
              <Link
                to="/dashboard"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
                style={{ background: C.turq, color: '#000000' }}
              >
                Get Started
              </Link>
            </div>
          </div>

          {/* Comparison note */}
          <p className="text-center text-xs mt-8" style={{ color: C.textMuted }}>
            Includes a verified on-profile badge, audit score, and full vulnerability report. &nbsp;
            <Link to="/projects" className="underline underline-offset-2 hover:text-white transition-colors" style={{ color: C.turq }}>
              See example audits →
            </Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: C.surface, borderTop: `1px solid ${C.border}` }} className="py-14">
        <div className="max-w-[720px] mx-auto px-5">
          <div className="text-center mb-10">
            <span
              className="text-xs font-semibold tracking-widest uppercase mb-3 inline-block"
              style={{ color: C.turq, letterSpacing: '0.12em' }}
            >
              FAQ
            </span>
            <h2 className="text-2xl font-light text-white" style={{ letterSpacing: '-0.5px' }}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            {FAQS.map((f) => <FAQ key={f.q} {...f} />)}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section
        className="py-14"
        style={{ background: C.bgDeep, borderTop: `1px solid ${C.border}` }}
      >
        <div className="max-w-[600px] mx-auto px-5 text-center flex flex-col items-center gap-5">
          <Shield className="w-10 h-10" style={{ color: C.turq }} />
          <h2 className="text-2xl font-light text-white" style={{ letterSpacing: '-0.5px' }}>
            Ready to get audited?
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>
            Create your project profile and submit your contract address. Our team will review and deliver your full audit report within 7–10 business days.
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
            style={{ background: C.turq, color: '#000000' }}
          >
            Create My Project Profile
          </Link>
        </div>
      </section>
    </Layout>
  )
}
